import { useState, useEffect, useCallback } from "react";
import { convertDecimalToHour } from "../../../../util/convertHours/convertDecimalToHour";
import { convertHourToDecimal } from "../../../../util/convertHours/convertHourToDecimal";

interface UseTimeRangeSliderParams {
    startTime?: string;
    endTime?: string;
    onTimeRangeChange: (startTime: string, endTime: string) => void;
    minRangeHours?: number;
}

interface UseTimeRangeSliderReturn {
    timeRange: [number, number];
    handleTimeRangeChange: (newValue: [number, number]) => void;
    handleTimeRangeChangeEnd: (newValue: [number, number]) => void;
    formatTimeLabel: (value: number) => string;
}

const DEFAULT_MIN_RANGE_HOURS = 1.0;
const DEFAULT_TIME_RANGE: [number, number] = [0, 24];
const MAX_HOUR_VALUE = 24;
const TIME_23_59 = "23:59";
const TIME_24_00 = "24:00";

/**
 * Custom hook to manage time range slider state and business logic
 * Handles time conversion, validation, and state synchronization
 */
export function useTimeRangeSlider({
    startTime,
    endTime,
    onTimeRangeChange,
    minRangeHours = DEFAULT_MIN_RANGE_HOURS,
}: UseTimeRangeSliderParams): UseTimeRangeSliderReturn {
    const [timeRange, setTimeRange] =
        useState<[number, number]>(DEFAULT_TIME_RANGE);

    /**
     * Converts time string to decimal format for slider
     */
    const convertTimeToDecimal = useCallback((time: string): number => {
        const normalizedTime = time === TIME_23_59 ? TIME_24_00 : time;
        return convertHourToDecimal(normalizedTime);
    }, []);

    /**
     * Converts decimal value to time string format
     */
    const convertDecimalToTime = useCallback((decimal: number): string => {
        return decimal === MAX_HOUR_VALUE
            ? TIME_23_59
            : convertDecimalToHour(decimal);
    }, []);

    /**
     * Formats time label for display (used in tooltips and marks)
     */
    const formatTimeLabel = useCallback(
        (value: number): string => {
            return convertDecimalToTime(value);
        },
        [convertDecimalToTime]
    );

    /**
     * Validates if the time range meets minimum requirements
     */
    const isValidRange = useCallback(
        (start: number, end: number): boolean => {
            const rangeDifference = end - start;
            return rangeDifference >= minRangeHours;
        },
        [minRangeHours]
    );

    /**
     * Initialize slider from external values when they change
     */
    useEffect(() => {
        if (startTime && endTime) {
            const startDecimal = convertTimeToDecimal(startTime);
            const endDecimal = convertTimeToDecimal(endTime);
            setTimeRange([startDecimal, endDecimal]);
        }
    }, [startTime, endTime, convertTimeToDecimal]);

    /**
     * Handle slider value change during drag (local state only)
     * This keeps the UI responsive without triggering parent updates
     */
    const handleTimeRangeChange = useCallback(
        (newValue: [number, number]) => {
            const [start, end] = newValue;

            // Prevent range less than minimum
            if (!isValidRange(start, end)) {
                return;
            }

            setTimeRange(newValue);
        },
        [isValidRange]
    );

    /**
     * Handle slider value change after drag ends
     * This notifies the parent component with the final values
     */
    const handleTimeRangeChangeEnd = useCallback(
        (newValue: [number, number]) => {
            const [start, end] = newValue;

            // Validate again for safety
            if (!isValidRange(start, end)) {
                return;
            }

            // Convert to HH:MM format and notify parent
            const startTimeFormatted = convertDecimalToTime(start);
            const endTimeFormatted = convertDecimalToTime(end);

            onTimeRangeChange(startTimeFormatted, endTimeFormatted);
        },
        [isValidRange, convertDecimalToTime, onTimeRangeChange]
    );

    return {
        timeRange,
        handleTimeRangeChange,
        handleTimeRangeChangeEnd,
        formatTimeLabel,
    };
}
